import { prefersReducedMotion, onDocumentReady, clamp } from '@theme/utilities';

/**
 * Pointer-driven micro-interactions for GripSway.
 *
 * Two independent effects, each gated by a body class that Liquid sets from a
 * theme setting, so nothing here runs unless the merchant opted in:
 *
 * - `card-hover-effect-tilt` / `card-hover-effect-spotlight` — cards react to
 *   where the cursor actually is, rather than to a binary :hover state. Both
 *   publish the same custom properties; only the CSS consuming them differs,
 *   so one pointer pipeline serves both.
 * - `magnetic-buttons` — primary buttons drift a few pixels toward the cursor
 *   as it approaches, then spring back on leave.
 *
 * Everything is expressed as custom properties (`--pointer-x`, `--tilt-x`, …)
 * that CSS turns into `transform`/`background-position`. This module never
 * touches layout properties and never reads geometry inside a move handler:
 * each element's rect is measured once on enter and reused until it leaves,
 * so a move costs arithmetic and a style write, not a reflow.
 *
 * Delegated from `document` so cards rendered later — AJAX filtering, infinite
 * scroll, product recommendations, Theme Editor section reloads — are picked up
 * with no re-initialisation and no per-card listeners.
 */

const CARD_SELECTOR = '.product-card, .collection-card';
const MAGNET_SELECTOR = '.button:not(.button-unstyled), .quick-add__button';

/** Maximum rotation in degrees at the very corner of a card. */
const MAX_TILT = 5;
/** Maximum distance in px a magnetic button travels toward the cursor. */
const MAX_MAGNET = 5;
/** How far outside a button the magnetic field reaches, in px. */
const MAGNET_FIELD = 28;

/** @typedef {{ el: HTMLElement, rect: DOMRect }} Tracked */

/** @type {Tracked | null} */
let card = null;
/** @type {Tracked | null} */
let magnet = null;

/** @type {number | null} */
let frame = null;
let pointerX = 0;
let pointerY = 0;

let tiltEnabled = false;
let spotlightEnabled = false;
let magnetEnabled = false;

/**
 * Writes the queued pointer position to whichever elements are currently
 * tracked. Runs at most once per animation frame no matter how many
 * `pointermove` events arrived in between.
 */
function render() {
  frame = null;

  if (card) {
    const { el, rect } = card;
    // -1 … 1 from the element's centre on each axis.
    const nx = rect.width ? clamp(((pointerX - rect.left) / rect.width) * 2 - 1, -1, 1) : 0;
    const ny = rect.height ? clamp(((pointerY - rect.top) / rect.height) * 2 - 1, -1, 1) : 0;

    if (tiltEnabled) {
      // Rotating around X responds to vertical movement, and is negated so the
      // card leans toward the cursor rather than away from it.
      el.style.setProperty('--tilt-x', `${(-ny * MAX_TILT).toFixed(2)}deg`);
      el.style.setProperty('--tilt-y', `${(nx * MAX_TILT).toFixed(2)}deg`);
    }

    if (spotlightEnabled) {
      el.style.setProperty('--pointer-x', `${(((nx + 1) / 2) * 100).toFixed(2)}%`);
      el.style.setProperty('--pointer-y', `${(((ny + 1) / 2) * 100).toFixed(2)}%`);
    }
  }

  if (magnet) {
    const { el, rect } = magnet;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    // Normalised against half the element plus the field, so the pull reaches
    // full strength right at the edge of the field and eases off toward centre.
    const dx = clamp((pointerX - cx) / (rect.width / 2 + MAGNET_FIELD), -1, 1);
    const dy = clamp((pointerY - cy) / (rect.height / 2 + MAGNET_FIELD), -1, 1);

    el.style.setProperty('--magnet-x', `${(dx * MAX_MAGNET).toFixed(2)}px`);
    el.style.setProperty('--magnet-y', `${(dy * MAX_MAGNET).toFixed(2)}px`);
  }
}

function schedule() {
  if (frame != null) return;
  frame = requestAnimationFrame(render);
}

/**
 * @param {Tracked | null} tracked
 */
function releaseCard(tracked) {
  if (!tracked) return;
  const { el } = tracked;
  el.classList.remove('pointer-effect--active');
  // Let CSS transition back to rest, then drop the properties entirely so the
  // element carries no inline style once the interaction is over.
  el.style.removeProperty('--tilt-x');
  el.style.removeProperty('--tilt-y');
}

/**
 * @param {Tracked | null} tracked
 */
function releaseMagnet(tracked) {
  if (!tracked) return;
  const { el } = tracked;
  el.classList.remove('pointer-effect--active');
  el.style.removeProperty('--magnet-x');
  el.style.removeProperty('--magnet-y');
}

/**
 * @param {PointerEvent} event
 */
function onPointerMove(event) {
  // Ignore coarse pointers even on hybrid devices: a touch drag should not
  // leave a card tilted after the finger lifts.
  if (event.pointerType !== 'mouse') return;

  pointerX = event.clientX;
  pointerY = event.clientY;

  const target = /** @type {Element | null} */ (event.target);

  if (tiltEnabled || spotlightEnabled) {
    const next = target?.closest?.(CARD_SELECTOR) ?? null;
    if (next !== card?.el) {
      releaseCard(card);
      card = next ? { el: /** @type {HTMLElement} */ (next), rect: next.getBoundingClientRect() } : null;
      if (card) card.el.classList.add('pointer-effect--active');
    }
  }

  if (magnetEnabled) {
    const next = target?.closest?.(MAGNET_SELECTOR) ?? null;
    if (next !== magnet?.el) {
      releaseMagnet(magnet);
      magnet = next ? { el: /** @type {HTMLElement} */ (next), rect: next.getBoundingClientRect() } : null;
      if (magnet) magnet.el.classList.add('pointer-effect--active');
    }
  }

  if (card || magnet) schedule();
}

function releaseAll() {
  releaseCard(card);
  releaseMagnet(magnet);
  card = null;
  magnet = null;
}

/**
 * A cached rect goes stale the moment the page scrolls or the viewport resizes.
 * Rather than re-measure on every scroll frame, drop tracking entirely — the
 * next `pointermove` re-acquires the element with a fresh rect.
 *
 * Registered in the capture phase: `scroll` does not bubble, and this theme
 * scrolls a nested container rather than the document, so a bubble-phase
 * listener on `document` would never fire and every rect would silently go
 * stale mid-scroll.
 */
function invalidate() {
  if (card || magnet) releaseAll();
}

function init() {
  if (prefersReducedMotion()) return;
  // Pointer effects are meaningless without a precise pointer, and running them
  // on touch would fire on every tap.
  if (!matchMedia('(any-pointer: fine)').matches) return;

  const { classList } = document.body;
  tiltEnabled = classList.contains('card-hover-effect-tilt');
  spotlightEnabled = classList.contains('card-hover-effect-spotlight');
  magnetEnabled = classList.contains('magnetic-buttons');

  if (!tiltEnabled && !spotlightEnabled && !magnetEnabled) return;

  document.addEventListener('pointermove', onPointerMove, { passive: true });
  document.addEventListener('pointerleave', releaseAll, { passive: true });
  document.addEventListener('scroll', invalidate, { passive: true, capture: true });
  window.addEventListener('resize', invalidate, { passive: true });
  // A card can be replaced under a stationary cursor (filter re-render); the
  // stale node would otherwise keep its active class forever.
  document.addEventListener('shopify:section:load', releaseAll);
}

onDocumentReady(init);
